
import { defineComponent, ref, PropType, computed } from 'vue'
import { GetAgreementBillingInfo } from '@/modules/agreements'
import {
    Address,
    ContactData,
    countryCodesList,
    DeliveryPolicy,
    deliveryPolicyTypeList,
    addressLabels,
    contactDataLabels,
    electronicLabels
} from '@/modules/billing-templates'
import AgreementsBillingInfoEditableFieldOrganizationName from '@/components/agreements/editable-fields/billing-info/organization-name.vue'
import AgreementsBillingInfoEditableFieldBusinessId from '@/components/agreements/editable-fields/billing-info/business-id.vue'
import AgreementsBillingInfoOpenDialogFieldAddress from '@/components/agreements/open-dialog-fields/billing-info/address.vue'
import AgreementsBillingInfoOpenDialogFieldsContactData from '@/components/agreements/open-dialog-fields/billing-info/contact-data.vue'
import { useElectronicOperatorsModule } from '@/modules/electronic-operators'
import AgreementsBillingInfoOpenDialogFieldDeliveryPolicy from '@/components/agreements/open-dialog-fields/billing-info/delivery-policy.vue'
import AgreementsBillingInfoEditableFieldPaymentTerms
    from '@/components/agreements/editable-fields/billing-info/payment-terms.vue'
import AgreementsBillingInfoEditableFieldInvoiceDescription
    from '@/components/agreements/editable-fields/billing-info/invoice-description.vue'
import AgreementsBillingInfoEditableFieldVatId from '@/components/agreements/editable-fields/billing-info/vat-id.vue'
import AgreementsBillingInfoEditableFieldCustomerNumber
    from '@/components/agreements/editable-fields/billing-info/customer-number.vue'
import AgreementsBillingInfoEditableFieldInvoiceReference
    from '@/components/agreements/editable-fields/billing-info/invoice-reference.vue'

export default defineComponent({
    name: 'agreement-show-edit-billing-info',
    components: {
        AgreementsBillingInfoEditableFieldInvoiceReference,
        AgreementsBillingInfoEditableFieldCustomerNumber,
        AgreementsBillingInfoEditableFieldVatId,
        AgreementsBillingInfoEditableFieldInvoiceDescription,
        AgreementsBillingInfoEditableFieldPaymentTerms,
        AgreementsBillingInfoEditableFieldOrganizationName,
        AgreementsBillingInfoEditableFieldBusinessId,
        AgreementsBillingInfoOpenDialogFieldAddress,
        AgreementsBillingInfoOpenDialogFieldsContactData,
        AgreementsBillingInfoOpenDialogFieldDeliveryPolicy
    },
    props: {
        agreementBillingInfo: {
            type: Object as PropType<GetAgreementBillingInfo>,
            required: true
        },
        agreementId: {
            type: String,
            required: true
        },
        billingInfoId: {
            type: String,
            required: true
        }
    },
    async setup (props) {
        const { getElectronicOperators, electronicOperators } = useElectronicOperatorsModule()
        const billingInfoModel = ref(props.agreementBillingInfo)

        // Address

        const billingInfoAddressModel = ref({
            street: props.agreementBillingInfo.street,
            city: props.agreementBillingInfo.city,
            zip: props.agreementBillingInfo.zip,
            countryCode: props.agreementBillingInfo.countryCode
        })

        const selectedCountry = computed(() => countryCodesList.find(item => item.value === billingInfoAddressModel.value.countryCode))

        const addressData = computed(() => {
            return {
                street: billingInfoAddressModel.value.street,
                city: billingInfoAddressModel.value.city,
                zip: billingInfoAddressModel.value.zip,
                countryCode: selectedCountry.value?.label
            }
        })

        const onUpdateAddressCallback = (newValue: Address) => {
            billingInfoAddressModel.value = newValue
        }

        // Contact data

        const billingInfoContactDataModel = ref({
            contactPersonEmail: props.agreementBillingInfo.contactPersonEmail,
            contactPersonName: props.agreementBillingInfo.contactPersonName
        })

        const onUpdateContactDataCallback = (newValue: ContactData) => {
            billingInfoContactDataModel.value = newValue
        }

        // Delivery policy

        const deliveryPolicyDataModel = ref({
            deliveryPolicyType: props.agreementBillingInfo.deliveryPolicyType,
            electronicOperatorId: props.agreementBillingInfo.deliveryPolicy?.electronicOperatorId,
            electronicAddress: props.agreementBillingInfo.deliveryPolicy?.electronicAddress,
            email: props.agreementBillingInfo.deliveryPolicy?.email
        })

        const selectedDeliveryPolicyType = computed(() => deliveryPolicyTypeList.find(item => item.value === deliveryPolicyDataModel.value.deliveryPolicyType))

        await getElectronicOperators()
        const selectedElectronicOperator = computed(() => electronicOperators.value.find(item => item.id.toString() === deliveryPolicyDataModel.value.electronicOperatorId))

        const electronicData = computed(() => {
            return {
                electronicAddress: deliveryPolicyDataModel.value.electronicAddress,
                electronicOperator: selectedElectronicOperator.value?.name
            }
        })

        const onUpdateDeliveryPolicyCallback = (newValue: DeliveryPolicy) => {
            deliveryPolicyDataModel.value = newValue
        }

        const isElectronic = computed(() => deliveryPolicyDataModel.value.deliveryPolicyType === 'electronic')
        const isEmail = computed(() => deliveryPolicyDataModel.value.deliveryPolicyType === 'email')

        return {
            billingInfoModel,
            billingInfoAddressModel,
            selectedCountry,
            billingInfoContactDataModel,
            deliveryPolicyDataModel,
            selectedDeliveryPolicyType,
            selectedElectronicOperator,
            onUpdateAddressCallback,
            onUpdateContactDataCallback,
            onUpdateDeliveryPolicyCallback,
            isElectronic,
            isEmail,
            electronicOperators,
            contactDataLabels,
            addressLabels,
            addressData,
            electronicData,
            electronicLabels
        }
    }
})
